package ui.screens

import androidx.compose.runtime.*
import org.jetbrains.compose.web.dom.*
import ui.components.Navbar
import ui.components.M3FilledButton
import data.FirebaseConfig

@Composable
fun VerificationScreen(
    id: String,
    typeFromUrl: String? = null,
    onBack: () -> Unit
) {
    var consultation by remember { mutableStateOf<dynamic>(null) }
    var doctor by remember { mutableStateOf<dynamic>(null) }
    var patient by remember { mutableStateOf<dynamic>(null) }
    var isLoading by remember { mutableStateOf(true) }
    var error by remember { mutableStateOf<String?>(null) }

    LaunchedEffect(id) {
        isLoading = true
        try {
            val consultData = FirebaseConfig.findConsultationById(id)
            if (consultData != null) {
                consultation = consultData
                val doctorId = consultData.doctorId as String
                val patientId = consultData.patientId as String
                
                doctor = FirebaseConfig.getDoctorData(doctorId)
                patient = FirebaseConfig.getPatientData(patientId)
            } else {
                error = "El documento con ID $id no fue encontrado."
            }
        } catch (e: Exception) {
            error = "Error al verificar el documento: ${e.message}"
        } finally {
            isLoading = false
        }
    }

    Div(attrs = {
        classes("min-h-screen", "bg-gray-100", "dark:bg-medical-bgDark", "transition-colors", "duration-300")
    }) {
        Navbar(onNavigateHome = onBack)

        Div(attrs = {
            classes("max-w-3xl", "mx-auto", "pt-32", "pb-20", "px-4")
        }) {
            if (isLoading) {
                Div(attrs = { classes("text-center", "py-20") }) {
                    Div(attrs = { classes("animate-spin", "rounded-full", "h-12", "w-12", "border-b-2", "border-medical-primary", "dark:border-medical-primaryDark", "mx-auto", "mb-4") })
                    Text("Verificando autenticidad...")
                }
            } else if (error != null) {
                Div(attrs = {
                    classes("bg-white", "dark:bg-medical-surfaceDark", "p-12", "rounded-[2.5rem]", "shadow-xl", "text-center", "border", "border-red-100", "dark:border-red-900/30")
                }) {
                    Span(attrs = { classes("material-symbols-outlined", "text-red-500", "dark:text-red-400", "text-6xl", "mb-6") }) { Text("error") }
                    H2(attrs = { classes("text-gray-900", "dark:text-medical-onSurfaceDark", "font-bold", "text-2xl", "mb-4") }) { Text("Verificación Fallida") }
                    P(attrs = { classes("text-red-600", "dark:text-red-400") }) { Text(error!!) }
                    M3FilledButton(attrs = {
                        classes("mt-6", "px-10")
                        onClick { onBack() }
                    }) { Text("Inicio") }
                }
            } else {
                // --- TARJETA DE CERTIFICADO CENTRAL ---
                Div(attrs = {
                    classes("bg-white", "dark:bg-medical-surfaceDark", "rounded-[2.5rem]", "shadow-2xl", "overflow-hidden", "relative", "border", "border-gray-200", "dark:border-medical-surfaceVariantDark")
                }) {
                    
                    // 1. HEADER: VALIDACIÓN Y TÍTULO (Usando color success 00897B)
                    Div(attrs = { classes("bg-green-50/30", "dark:bg-green-900/10", "p-8", "md:p-12", "border-b", "border-gray-100", "dark:border-medical-surfaceVariantDark") }) {
                        Div(attrs = { classes("flex", "items-center", "gap-2", "bg-white", "dark:bg-medical-bgDark", "w-fit", "px-4", "py-1.5", "rounded-full", "mb-6", "border", "border-green-200", "dark:border-green-900/50", "shadow-sm") }) {
                            Span(attrs = { 
                                classes("material-symbols-outlined", "text-[#00897B]", "dark:text-medical-successDark", "text-lg") 
                                style { property("font-variation-settings", "'FILL' 1") }
                            }) { Text("verified") }
                            Span(attrs = { classes("text-[#00897B]", "dark:text-medical-successDark", "text-[11px]", "font-black", "uppercase", "tracking-wider") }) { Text("Certificado Digital Validado") }
                        }

                        Div(attrs = { classes("flex", "flex-col", "md:flex-row", "md:items-end", "justify-between", "gap-4") }) {
                            Div {
                                H1(attrs = { classes("text-3xl", "md:text-4xl", "font-black", "text-gray-900", "dark:text-medical-onSurfaceDark") }) {
                                    Text(getDocumentType(consultation, typeFromUrl))
                                }
                                Div(attrs = { classes("mt-2", "flex", "items-center", "gap-2") }) {
                                    Span(attrs = { classes("text-gray-400", "dark:text-gray-500", "text-xs", "uppercase", "font-bold") }) { Text("Fecha Emisión:") }
                                    Span(attrs = { classes("text-gray-600", "dark:text-gray-300", "font-bold", "text-sm") }) {
                                        Text(formatFirestoreDate(consultation?.consultationDate))
                                    }
                                }
                            }
                        }
                    }

                    // 2. CUERPO PRINCIPAL: TRATAMIENTO O CONTENIDO
                    Div(attrs = { classes("p-8", "md:p-12") }) {
                        val docType = getDocumentType(consultation, typeFromUrl)
                        
                        Div(attrs = { classes("mb-12") }) {
                        // --- CASO RÉCIPE ---
                        if (docType.contains("Récipe")) {
                                val medicines = consultation.recipe?.medicines as? Array<dynamic>
                                if (!medicines.isNullOrEmpty()) {
                                    Div {
                                        SectionHeader("medication", "Tratamiento Indicado")
                                        Div(attrs = { classes("grid", "grid-cols-1", "gap-6", "mt-6") }) {
                                            medicines.forEach { med ->
                                                val name = med.name as? String ?: ""
                                                val pres = med.presentation as? String ?: ""
                                                // pr-24 para evitar choque con icono en móviles
                                                Div(attrs = { classes("p-8", "pr-24", "bg-medical-primary/5", "dark:bg-medical-primaryDark/5", "rounded-[2rem]", "border", "border-medical-primary/10", "dark:border-medical-primaryDark/10", "relative") }) {
                                                    P(attrs = { classes("font-black", "text-gray-900", "dark:text-medical-onSurfaceDark", "text-2xl", "md:text-3xl", "leading-tight") }) { Text(name) }
                                                    if (pres.isNotBlank()) {
                                                        P(attrs = { classes("text-lg", "text-gray-500", "dark:text-gray-400", "mt-2", "font-medium") }) { Text(pres) }
                                                    }
                                                    Span(attrs = { classes("material-symbols-outlined", "absolute", "top-8", "right-8", "text-medical-primary/10", "dark:text-medical-primaryDark/10", "text-5xl") }) { Text("pill") }
                                                }
                                            }
                                        }
                                    }
                                }
                            }

                            // --- CASO INFORME / REPOSO ---
                            if (docType.contains("Informe") || docType.contains("Reposo")) {
                                val diags = getDiagnostics(consultation)
                                if (diags.isNotEmpty()) {
                                    Div {
                                        SectionHeader("clinical_notes", "Diagnóstico Clínico")
                                        Div(attrs = { classes("mt-6", "space-y-4") }) {
                                            diags.forEach { dx -> 
                                                val dxText = dx as? String ?: ""
                                                if (dxText.isNotBlank()) {
                                                    Div(attrs = { classes("flex", "items-start", "gap-4", "bg-gray-50", "dark:bg-gray-800/50", "p-6", "rounded-2xl", "border", "border-gray-100", "dark:border-gray-700") }) {
                                                        Span(attrs = { classes("material-symbols-outlined", "text-medical-primary", "dark:text-medical-primaryDark", "mt-1") }) { Text("check_circle") }
                                                        P(attrs = { classes("text-lg", "text-gray-700", "dark:text-gray-300", "font-semibold") }) { Text(dxText) }
                                                    }
                                                }
                                            }
                                        }
                                    }
                                }
                                
                                if (docType.contains("Reposo")) {
                                    val days = consultation.reposo?.days as? String ?: ""
                                    if (days.isNotBlank()) {
                                        // --- CÁLCULO DEL RANGO (Sincronizado con App Android) ---
                                        val dateInfo = remember(days, consultation.consultationDate) {
                                            try {
                                                val daysCount = days.filter { it.isDigit() }.toIntOrNull() ?: 0
                                                if (daysCount > 0 && consultation.consultationDate != null) {
                                                    val start = consultation.consultationDate.toDate()
                                                    val startTime = start.getTime() as Double
                                                    
                                                    // Fin del reposo (Día - 1)
                                                    val end = kotlin.js.Date(startTime + ((daysCount - 1) * 86400000.0))
                                                    // Reincorporación (Día exacto)
                                                    val returnDate = kotlin.js.Date(startTime + (daysCount * 86400000.0))
                                                    
                                                    object {
                                                        val range = "${start.toLocaleDateString()} — ${end.toLocaleDateString()}"
                                                        val returnDay = returnDate.toLocaleDateString()
                                                    }
                                                } else null
                                            } catch (_: Exception) { null }
                                        }

                                        Div(attrs = { classes("mt-8", "bg-amber-50", "dark:bg-amber-900/10", "p-8", "rounded-[2rem]", "border", "border-amber-100", "dark:border-amber-900/30", "text-center") }) {
                                            SectionLabel("Días de Reposo Autorizados")
                                            P(attrs = { classes("text-4xl", "font-black", "text-amber-900", "dark:text-amber-200", "mt-2") }) { Text("$days días") }
                                            
                                            dateInfo?.let { info ->
                                                P(attrs = { classes("text-sm", "text-amber-800", "dark:text-amber-300", "font-bold", "mt-4", "uppercase", "tracking-wider") }) {
                                                    Text("Vigencia: ${info.range}")
                                                }
                                                P(attrs = { classes("text-[10px]", "text-amber-700/60", "dark:text-amber-400/60", "font-black", "mt-1", "uppercase") }) {
                                                    Text("Reincorporación: ${info.returnDay}")
                                                }
                                            }
                                        }
                                    }
                                }
                            }

                            // --- CASO ESTUDIOS ---
                            if (docType.contains("Orden de Estudio")) {
                                val lab = consultation.paraclinico?.studies as? Array<dynamic>
                                val img = consultation.paraclinico?.imagingStudies as? Array<dynamic>
                                val oth = consultation.paraclinico?.otherStudies as? Array<dynamic>

                                if (!lab.isNullOrEmpty()) StudyGroupSection("Exámenes de Laboratorio", lab, "biotech")
                                if (!img.isNullOrEmpty()) StudyGroupSection("Estudios de Imágenes", img, "image")
                                if (!oth.isNullOrEmpty()) StudyGroupSection("Otros Estudios", oth, "clinical_notes")
                            }
                        }

                        // 3. FOOTER DE LA TARJETA: DATOS DE CONTROL (ORDEN: PACIENTE -> MÉDICO)
                        Div(attrs = { classes("pt-10", "border-t", "border-gray-100", "dark:border-medical-surfaceVariantDark", "grid", "grid-cols-1", "md:grid-cols-2", "gap-10") }) {
                            // Columna Paciente (Primero)
                            Div {
                                SectionLabel("Paciente (Identidad Protegida)")
                                P(attrs = { classes("text-lg", "font-bold", "text-gray-800", "dark:text-gray-200", "mt-1") }) {
                                    Text(formatPatientName(patient?.name as? String, patient?.lastName as? String))
                                }
                                P(attrs = { classes("text-sm", "text-gray-500", "dark:text-gray-400", "font-bold") }) {
                                    val pId = (patient?.idNumber as? String) ?: ""
                                    val maskedId = if (pId.length >= 4) "****${pId.takeLast(4)}" else pId
                                    Text("CI: $maskedId")
                                }
                            }

                            // Columna Médico (Segundo)
                            Div {
                                SectionLabel("Profesional de la Salud")
                                val doctorAbbr = doctor?.abbreviation as? String ?: "Dr."
                                val doctorName = doctor?.fullName as? String ?: "No disponible"
                                P(attrs = { classes("text-lg", "font-bold", "text-gray-800", "dark:text-gray-200", "mt-1") }) {
                                    Text("$doctorAbbr $doctorName")
                                }
                                P(attrs = { classes("text-medical-primary", "dark:text-medical-primaryDark", "font-bold", "text-xs", "uppercase", "tracking-wider", "mt-1") }) {
                                    val mMatricula = doctor?.nMatricula as? String ?: "N/A"
                                    Text("${doctor?.speciality ?: ""} | MPPS: $mMatricula")
                                }
                            }
                        }

                        // --- NOTA FINAL DE SEGURIDAD E INTEGRIDAD ---
                        Div(attrs = {
                            classes("mt-16", "pt-10", "border-t", "border-gray-100", "dark:border-medical-surfaceVariantDark", "text-center")
                        }) {
                            Span(attrs = { classes("text-[10px]", "font-black", "text-medical-primary/50", "dark:text-medical-primaryDark/50", "uppercase", "tracking-[0.2em]") }) {
                                Text("Verificación de Seguridad")
                            }
                            P(attrs = { classes("text-xs", "text-gray-500", "dark:text-gray-400", "leading-relaxed", "italic", "mt-4", "max-w-md", "mx-auto") }) {
                                Text("Este certificado oficial confirma que los datos mostrados en esta pantalla coinciden exactamente con el registro original almacenado en los servidores seguros de DrRecipe. Cualquier alteración física de este documento invalida su autenticidad.")
                            }
                        }
                    }
                }

                // Banner Inferior Externo
                Div(attrs = { classes("mt-12", "text-center", "pb-10") }) {
                    M3FilledButton(attrs = {
                        classes("px-10")
                        onClick { onBack() }
                    }) { Text("Inicio") }
                    
                    P(attrs = { classes("text-sm", "text-gray-400", "dark:text-gray-500", "mt-8") }) {
                        Text("Generado vía ")
                        Span(attrs = { classes("font-black", "text-medical-primary", "dark:text-medical-primaryDark") }) { Text("DrRecipe App") }
                    }
                }
            }
        }
    }
}

@Composable
private fun SectionLabel(label: String) {
    Span(attrs = { classes("text-[10px]", "font-black", "text-gray-400", "dark:text-gray-500", "uppercase", "tracking-[0.15em]") }) {
        Text(label)
    }
}

@Composable
private fun SectionHeader(icon: String, title: String) {
    Div(attrs = { classes("flex", "items-center", "gap-3", "pb-3", "border-b-2", "border-gray-50", "dark:border-gray-800/50") }) {
        Span(attrs = { 
            classes("material-symbols-outlined", "text-2xl") 
            style { property("color", "#80CBC4") } // Aquí puedes usar el color si lo deseas o clases de Tailwind
        }) { Text(icon) }
        H4(attrs = { classes("text-xl", "font-black", "text-gray-900", "dark:text-medical-onSurfaceDark") }) { Text(title) }
    }
}

@Composable
private fun StudyGroupSection(title: String, items: Array<dynamic>, icon: String) {
    Div(attrs = { classes("mt-8") }) {
        SectionHeader(icon, title)
        Div(attrs = { classes("grid", "grid-cols-1", "gap-3", "mt-6") }) {
            items.forEach { item ->
                val text = item as? String ?: ""
                if (text.isNotBlank()) {
                    Div(attrs = { classes("flex", "items-center", "gap-4", "bg-gray-50", "dark:bg-gray-800/50", "px-6", "py-4", "rounded-2xl", "text-sm", "text-gray-700", "dark:text-gray-300", "border", "border-gray-100", "dark:border-gray-700") }) {
                        Span(attrs = { 
                            classes("material-symbols-outlined", "text-xs") 
                            style { property("color", "#80CBC4") }
                        }) { Text("arrow_forward") }
                        Text(text)
                    }
                }
            }
        }
    }
}

private fun formatPatientName(firstName: String?, lastName: String?): String {
    if (firstName == null || lastName == null) return "Paciente no disponible"
    
    fun process(text: String): String {
        val parts = text.trim().split(" ").filter { it.isNotBlank() }
        if (parts.isEmpty()) return ""
        if (parts.size <= 2) return text.trim()
        
        return parts[0] + " " + parts.drop(1).joinToString(" ") { it.take(1) + "." }
    }

    val f = process(firstName)
    val l = process(lastName)
    return "$f $l"
}

private fun getDiagnostics(consultation: dynamic): Array<dynamic> {
    if (consultation == null) return emptyArray()
    return (consultation.informe?.diagnostics as? Array<dynamic>)
        ?: (consultation.reposo?.diagnostics as? Array<dynamic>)
        ?: emptyArray()
}

private fun getDocumentType(consultation: dynamic, typeFromUrl: String?): String {
    if (consultation == null) return "Documento Médico"

    when (typeFromUrl) {
        "recipe" -> return "Récipe Médico"
        "reposo" -> return "Reposo Médico"
        "informe" -> return "Informe Médico"
        "paraclinico" -> return "Orden de Estudio"
    }

    if (consultation.recipe != null) return "Récipe Médico"
    if (consultation.reposo != null) return "Reposo Médico"
    if (consultation.informe != null) return "Informe Médico"
    if (consultation.paraclinico != null) return "Orden de Estudio"

    return "Documento Médico"
}

private fun formatFirestoreDate(timestamp: dynamic): String {
    if (timestamp == null) return "Fecha no disponible"
    return try {
        // En JS, si es un Timestamp de Firebase tiene .toDate()
        val date = if (timestamp.toDate != undefined) timestamp.toDate() else timestamp
        val d = date as kotlin.js.Date
        
        // Extraemos componentes para evitar desfases de zona horaria al formatear
        val day = d.getDate().toString().padStart(2, '0')
        val month = (d.getMonth() + 1).toString().padStart(2, '0')
        val year = d.getFullYear()
        
        "$day/$month/$year"
    } catch (_: Exception) {
        "Fecha no válida"
    }
}
